<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import StudioBadge from './StudioBadge.vue';
import { barWidthClass } from './barWidth';

const props = defineProps({
  trace: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const stages = computed(() => props.trace?.stages || []);

const llm = computed(() => props.trace?.llm || null);

const guard = computed(() => props.trace?.guard || null);

const apiDebug = computed(() => props.trace?.api_debug || null);

const tokenSummary = computed(() => {
  if (!llm.value) return '';
  const parts = [];
  if (llm.value.input_tokens != null)
    parts.push(`in ${llm.value.input_tokens}`);
  if (llm.value.output_tokens != null)
    parts.push(`out ${llm.value.output_tokens}`);
  if (llm.value.cached_tokens) parts.push(`cached ${llm.value.cached_tokens}`);
  return parts.join(' · ');
});

const totalMs = computed(() =>
  props.trace ? `${props.trace.total_ms} ms` : ''
);

const stageRows = computed(() => {
  const slowest = Math.max(0, ...stages.value.map(stage => stage.ms || 0));
  return stages.value.map(stage => ({
    ...stage,
    displayMs: `${stage.ms} ms`,
    widthClass: barWidthClass(stage.ms, slowest),
    // A stage that took no time and resolved nothing is noise — dim it.
    isIdle: !stage.hit && !stage.ms,
  }));
});

const llmModel = computed(() => (llm.value ? t('AI_AGENT.ENGINE_NAME') : ''));

const apiDebugRequest = computed(() => {
  if (!apiDebug.value) return '';
  return [apiDebug.value.request?.method, apiDebug.value.request?.url]
    .filter(Boolean)
    .join(' ');
});

const apiDebugResponse = computed(() =>
  apiDebug.value ? `⇒ ${apiDebug.value.response?.status ?? ''}` : ''
);

const guardSummary = computed(() => {
  if (!guard.value) return '';
  const state = guard.value.blocked
    ? t('AI_AGENT.PLAYGROUND.TRACE.GUARD.BLOCKED')
    : t('AI_AGENT.PLAYGROUND.TRACE.GUARD.PASSED');
  return [state, guard.value.reason].filter(Boolean).join(' · ');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <span class="i-lucide-activity size-4 text-n-slate-10" />
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('AI_AGENT.PLAYGROUND.TRACE.TITLE') }}
      </h3>
    </div>
    <div
      v-if="!trace"
      class="flex flex-col items-center gap-2 px-4 py-10 text-center border border-dashed rounded-xl border-n-strong"
    >
      <span class="i-lucide-route size-6 text-n-slate-9" />
      <p class="max-w-xs text-body-main text-n-slate-11">
        {{ $t('AI_AGENT.PLAYGROUND.TRACE.EMPTY') }}
      </p>
    </div>
    <div v-else class="flex flex-col gap-4" data-testid="trace-timeline">
      <dl class="grid grid-cols-2 gap-2">
        <div
          class="flex flex-col col-span-2 gap-0.5 px-3 py-2 rounded-lg bg-n-brand/10"
        >
          <dt class="text-label-small text-n-blue-11">
            {{ $t('AI_AGENT.PLAYGROUND.TRACE.RESOLVED_BY') }}
          </dt>
          <dd
            class="font-mono text-heading-3 text-n-slate-12"
            data-testid="trace-resolved-by"
          >
            {{ trace.resolved_by }}
          </dd>
        </div>
        <div class="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-n-alpha-1">
          <dt class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.PLAYGROUND.TRACE.TOTAL_MS') }}
          </dt>
          <dd class="tabular-nums text-heading-3 text-n-slate-12">
            {{ totalMs }}
          </dd>
        </div>
        <div
          v-if="llm && llm.confidence != null"
          class="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-n-alpha-1"
        >
          <dt class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.PLAYGROUND.TRACE.LLM.CONFIDENCE') }}
          </dt>
          <dd class="tabular-nums text-heading-3 text-n-slate-12">
            {{ Math.round(llm.confidence * 100) }}%
          </dd>
        </div>
        <div
          v-if="llmModel"
          class="flex flex-col col-span-2 gap-0.5 px-3 py-2 rounded-lg bg-n-alpha-1"
        >
          <dt class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.PLAYGROUND.TRACE.LLM.TITLE') }}
          </dt>
          <dd class="flex flex-wrap items-center gap-1.5">
            <span class="truncate text-body-main text-n-slate-12">
              {{ llmModel }}
            </span>
            <StudioBadge v-if="llm.fallback_used" tone="amber">
              {{ $t('AI_AGENT.PLAYGROUND.TRACE.LLM.FALLBACK') }}
            </StudioBadge>
            <StudioBadge v-if="tokenSummary">
              <span class="i-lucide-coins size-3" />
              {{ tokenSummary }}
            </StudioBadge>
          </dd>
        </div>
      </dl>

      <div
        v-if="trace.error"
        class="flex items-start gap-2 p-3 rounded-lg text-body-main bg-n-ruby-9/10 text-n-ruby-11"
      >
        <span class="i-lucide-triangle-alert size-4 mt-0.5 shrink-0" />
        <span class="break-words">
          {{ $t('AI_AGENT.PLAYGROUND.TRACE.ERROR') }}: {{ trace.error }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <h4 class="text-label-small text-n-slate-10">
          {{ $t('AI_AGENT.PLAYGROUND.TRACE.STAGES') }}
        </h4>
        <ol class="flex flex-col gap-0.5">
          <li
            v-for="stage in stageRows"
            :key="stage.name"
            class="grid items-center grid-cols-[minmax(0,8rem)_1fr_auto] gap-3 px-2 py-1.5 rounded-lg"
            :class="[
              stage.hit
                ? 'bg-n-brand/10 outline outline-1 -outline-offset-1 outline-n-brand/30'
                : '',
              stage.isIdle ? 'opacity-50' : '',
            ]"
            :data-testid="`trace-stage-${stage.name}`"
          >
            <span class="flex items-center min-w-0 gap-2">
              <span
                class="rounded-full size-1.5 shrink-0"
                :class="stage.hit ? 'bg-n-brand' : 'bg-n-slate-8'"
              />
              <span
                class="font-mono truncate text-label-small"
                :class="stage.hit ? 'text-n-slate-12' : 'text-n-slate-11'"
              >
                {{ stage.name }}
              </span>
            </span>
            <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-1.5 rounded-full"
                :class="[
                  stage.widthClass,
                  stage.hit ? 'bg-n-brand' : 'bg-n-slate-8',
                ]"
              />
            </div>
            <span class="flex items-center justify-end gap-1.5">
              <StudioBadge v-if="stage.hit" tone="blue">
                {{ $t('AI_AGENT.PLAYGROUND.TRACE.HIT') }}
              </StudioBadge>
              <span
                class="w-16 text-right tabular-nums text-label-small"
                :class="stage.hit ? 'text-n-slate-12' : 'text-n-slate-10'"
              >
                {{ stage.displayMs }}
              </span>
            </span>
          </li>
        </ol>
      </div>

      <details
        v-if="apiDebug"
        class="group rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <summary
          class="flex items-center gap-2 px-3 py-2 list-none cursor-pointer select-none text-label text-n-slate-12 [&::-webkit-details-marker]:hidden"
        >
          <span
            class="transition-transform i-lucide-chevron-right size-4 text-n-slate-10 group-open:rotate-90 motion-reduce:transition-none"
          />
          {{ $t('AI_AGENT.PLAYGROUND.TRACE.API_DEBUG') }}
          <StudioBadge
            class="ml-auto"
            :tone="apiDebug.response?.status >= 400 ? 'ruby' : 'teal'"
          >
            {{ apiDebug.response?.status ?? '—' }}
          </StudioBadge>
        </summary>
        <div class="flex flex-col gap-1 px-3 pb-3 font-mono text-label-small">
          <p class="break-all text-n-slate-12">{{ apiDebugRequest }}</p>
          <p class="text-n-slate-11">{{ apiDebugResponse }}</p>
        </div>
      </details>

      <details
        v-if="guard"
        class="group rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
        :open="guard.blocked"
      >
        <summary
          class="flex items-center gap-2 px-3 py-2 list-none cursor-pointer select-none text-label text-n-slate-12 [&::-webkit-details-marker]:hidden"
        >
          <span
            class="transition-transform i-lucide-chevron-right size-4 text-n-slate-10 group-open:rotate-90 motion-reduce:transition-none"
          />
          {{ $t('AI_AGENT.PLAYGROUND.TRACE.GUARD.TITLE') }}
          <StudioBadge
            class="ml-auto"
            dot
            :tone="guard.blocked ? 'ruby' : 'teal'"
          >
            {{
              guard.blocked
                ? $t('AI_AGENT.PLAYGROUND.TRACE.GUARD.BLOCKED')
                : $t('AI_AGENT.PLAYGROUND.TRACE.GUARD.PASSED')
            }}
          </StudioBadge>
        </summary>
        <p class="px-3 pb-3 text-body-main text-n-slate-11">
          {{ guardSummary }}
        </p>
      </details>
    </div>
  </div>
</template>
